import { useState } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import type { ActivityCardSlice, ActivityDraft } from '@/chat/activity';
import type { ChatMessage } from '@/chat/types';
import { ACTIVITY_AREA_HINT_AFTER, ACTIVITY_HINT_AFTER, pastWrongs } from '@/chat/reducer';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { PlainText } from '../rich-text';
import { useActivityDraft, useCardDraft } from './draft';
import { DragSurface } from './drag-targets';
import { HotspotSurface } from './hotspot';
import { OrderingSurface } from './ordering';

/**
 * An activity as a message: an interactive card while it is armed, and a
 * record of what the learner did once it is not.
 *
 * The card only ever draws. Marking happens in the footer (`AnswerControl`),
 * which is the component the reducer hands the prompt to; the two share the
 * draft store. A card that is not the armed one is inert — before it arms
 * because the coach is still talking, after it because the answer is in.
 *
 * A picture activity can also open full screen. The dialog renders the same
 * surface on the same draft, so it needs no Check of its own.
 */

export type ActivityCardProps = { message: Extract<ChatMessage, { kind: 'activity' }> };

export function ActivityCard({ message }: ActivityCardProps) {
  const { chat } = useStrings();
  const { slice, answered, revealed } = message;
  const { draft, armed, attempt } = useCardDraft(message.id);
  const update = useActivityDraft((state) => state.update);

  // Hints show only while armed. The text appears after one miss, the area after two.
  const hintText =
    armed && slice.variation !== 'free_text' && pastWrongs(attempt, ACTIVITY_HINT_AFTER)
      ? slice.primary.hint?.trim()
      : undefined;
  const areaHint = armed && pastWrongs(attempt, ACTIVITY_AREA_HINT_AFTER);
  const [expanded, setExpanded] = useState(false);
  const expandable = slice.variation === 'hotspot' || slice.variation === 'drag_targets';

  return (
    <figure
      className={cn(
        'bg-bubble-buddy w-full max-w-full overflow-hidden rounded-2xl rounded-es-md',
        'shadow-[0_1px_2px_rgb(16_24_64/0.06),0_4px_12px_-6px_rgb(16_24_64/0.14)]',
        // A ring, not a border: it cannot move the card as the state changes.
        armed && 'ring-surf-yellow ring-2',
      )}
    >
      {/* Fixed row height, so gaining or losing the tag never shifts the card. */}
      <figcaption className="flex min-h-11 items-center justify-between gap-2 px-4 py-2">
        <span className="text-chat-ink flex items-center gap-2 text-[13px] font-bold">
          {slice.variation === 'free_text' && answered ? (
            <Icon name="passport/check" className="text-answer-correct size-4 shrink-0" />
          ) : null}
          {/* Free text has no canvas, so its card is the question it asked. */}
          <PlainText field={slice.variation === 'free_text' ? slice.primary.prompt : slice.primary.guide} />
        </span>
        <Tag
          state={armed ? (attempt > 1 ? 'retry' : 'none') : revealed ? 'shown' : answered ? 'correct' : 'none'}
          retryLabel={formatString(chat.chat_attempt_count, { current: attempt })}
        />
      </figcaption>

      {slice.variation === 'free_text' ? (
        // No canvas to draw: the guide is the whole body, and the answer lands
        // as the learner's own bubble under the card.
        <p className="text-chat-ink-muted px-4 pb-3 text-[13px]">
          <PlainText field={slice.primary.guide} />
        </p>
      ) : (
        <div className="relative px-3 pb-3">
          <Surface slice={slice} draft={draft} armed={armed} revealed={revealed} hint={areaHint} onChange={update} />
          {expandable ? (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-label={chat.chat_activity_expand}
              className="chat-pressable focus-visible:ring-surf-blue/40 absolute end-5 top-2 grid size-9 place-items-center rounded-full bg-black/50 text-white backdrop-blur-[2px] focus-visible:ring-[3px] focus-visible:outline-none"
            >
              <Icon name="passport/expand" aria-hidden className="size-4" />
            </button>
          ) : null}
        </div>
      )}

      {expandable ? (
        <DialogPrimitive.Root open={expanded} onOpenChange={setExpanded}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Overlay className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80 duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)]" />
            <DialogPrimitive.Content
              data-slot="chat-activity-expand"
              className="bp-overlay data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 flex flex-col p-[max(0.75rem,env(safe-area-inset-top))_0.75rem_max(0.75rem,env(safe-area-inset-bottom))] text-white duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)]"
            >
              <div className="flex items-center justify-between gap-3">
                <DialogPrimitive.Title className="min-w-0 truncate text-sm font-bold">
                  <PlainText field={slice.primary.guide} />
                </DialogPrimitive.Title>
                <DialogPrimitive.Close asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={chat.chat_image_close}
                    className="size-11 shrink-0 text-white hover:bg-white/15 active:bg-white/25"
                  >
                    <Icon name="passport/close" aria-hidden className="size-5" />
                  </Button>
                </DialogPrimitive.Close>
              </div>
              <DialogPrimitive.Description className="sr-only">{chat.chat_activity_expand}</DialogPrimitive.Description>

              {/* The stage keeps the picture's ratio, so capping the width caps the height. */}
              <div className="grid min-h-0 flex-1 place-items-center">
                <div className="w-full max-w-[calc((100dvh-8rem)*4/3)]">
                  <Surface
                    slice={slice}
                    draft={draft}
                    armed={armed}
                    revealed={revealed}
                    hint={areaHint}
                    onChange={update}
                  />
                </div>
              </div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      ) : null}

      {hintText ? (
        <p className="text-chat-ink flex items-start gap-2 px-4 pb-3 text-[13px] leading-snug">
          <Icon name="passport/lifesaver" aria-hidden className="text-surf-yellow mt-0.5 size-4 shrink-0" />
          <span>{hintText}</span>
        </p>
      ) : null}
    </figure>
  );
}

/** The card's state, said in words. Colour is never the only cue. */
function Tag({ state, retryLabel }: { state: 'none' | 'retry' | 'correct' | 'shown'; retryLabel: string }) {
  const { chat } = useStrings();

  if (state === 'none') {
    return null;
  }

  const label =
    state === 'retry' ? retryLabel : state === 'correct' ? chat.chat_activity_correct : chat.chat_activity_shown;

  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold',
        state === 'retry' && 'bg-surf-yellow/25 text-chat-ink',
        state === 'correct' && 'bg-answer-correct/12 text-answer-correct',
        state === 'shown' && 'bg-answer-wrong/10 text-answer-wrong',
      )}
    >
      {state === 'correct' ? <Icon name="passport/check" className="size-3" /> : null}
      {label}
    </span>
  );
}

type SurfaceProps = {
  slice: ActivityCardSlice;
  draft: ActivityDraft | null;
  armed: boolean;
  revealed: boolean;
  /** Draw the surface's own hint: a search area, or the first step called out. */
  hint: boolean;
  onChange: (draft: ActivityDraft) => void;
};

/** Dispatch on the variation. Each surface owns its own geometry and motion. */
function Surface({ slice, draft, armed, revealed, hint, onChange }: SurfaceProps) {
  if (slice.variation === 'hotspot') {
    return (
      <HotspotSurface
        slice={slice}
        point={draft?.kind === 'hotspot' ? draft.point : null}
        armed={armed}
        revealed={revealed}
        hint={hint}
        onChange={(point) => onChange({ kind: 'hotspot', point })}
      />
    );
  }

  if (slice.variation === 'drag_targets') {
    return (
      <DragSurface
        slice={slice}
        points={draft?.kind === 'drag' ? draft.points : []}
        armed={armed}
        revealed={revealed}
        hint={hint}
        onChange={(points) => onChange({ kind: 'drag', points })}
      />
    );
  }

  return (
    <OrderingSurface
      slice={slice}
      sequence={draft?.kind === 'ordering' ? draft.sequence : []}
      armed={armed}
      revealed={revealed}
      hint={hint}
      onChange={(sequence) => onChange({ kind: 'ordering', sequence })}
    />
  );
}
